import * as THREE from 'three';
import type { Terrain } from '../world/Terrain';
import type { TeamId, Vec3 } from '@use/shared';
import {
  INFANTRY_HP, INFANTRY_MOVE_SPEED, INFANTRY_SPRINT_SPEED,
  INFANTRY_JUMP_FORCE, INFANTRY_HEIGHT, INFANTRY_RADIUS, isInBounds,
  INFANTRY_MAX_SLOPE_DEG, SLOPE_SLIDE_FORCE,
  getSlopeAngle, getSlopeSpeedMultiplier, getDownhillDirection,
} from '@use/shared';

export class Infantry {
  public group: THREE.Group;
  public body: THREE.Mesh;
  public head: THREE.Mesh;
  public gunMesh: THREE.Mesh;

  public playerId: string;
  public teamId: TeamId;
  public hp: number = INFANTRY_HP;
  public alive: boolean = true;

  // Physics state
  public velocity = new THREE.Vector3();
  public onGround = false;
  public yaw = 0;
  public pitch = 0;

  private nameTag: THREE.Sprite | null = null;

  constructor(
    scene: THREE.Scene,
    playerId: string,
    teamId: TeamId,
    position: Vec3,
    playerName: string = 'Player'
  ) {
    this.playerId = playerId;
    this.teamId = teamId;
    this.group = new THREE.Group();
    this.group.position.set(position.x, position.y, position.z);

    const teamColor = teamId === 'red' ? 0xcc3333 : 0x3366cc;

    // Body — capsule-like (box body)
    const bodyGeo = new THREE.BoxGeometry(0.6, 1.2, 0.4);
    const bodyMat = new THREE.MeshLambertMaterial({ color: teamColor, flatShading: true });
    this.body = new THREE.Mesh(bodyGeo, bodyMat);
    this.body.position.y = 0.9;
    this.body.castShadow = true;
    this.group.add(this.body);

    // Head — sphere
    const headGeo = new THREE.SphereGeometry(0.25, 8, 6);
    const headMat = new THREE.MeshLambertMaterial({ color: 0xddbb88, flatShading: true });
    this.head = new THREE.Mesh(headGeo, headMat);
    this.head.position.y = 1.75;
    this.head.castShadow = true;
    this.group.add(this.head);

    // Arms — simple cylinders
    const armGeo = new THREE.BoxGeometry(0.2, 0.8, 0.2);
    const armMat = new THREE.MeshLambertMaterial({ color: teamColor, flatShading: true });
    const leftArm = new THREE.Mesh(armGeo, armMat);
    leftArm.position.set(-0.5, 1.0, 0);
    this.group.add(leftArm);

    const rightArm = new THREE.Mesh(armGeo, armMat);
    rightArm.position.set(0.5, 1.0, 0);
    this.group.add(rightArm);

    // Legs
    const legGeo = new THREE.BoxGeometry(0.22, 0.8, 0.22);
    const legMat = new THREE.MeshLambertMaterial({ color: 0x444444, flatShading: true });
    const leftLeg = new THREE.Mesh(legGeo, legMat);
    leftLeg.position.set(-0.18, 0.2, 0);
    this.group.add(leftLeg);

    const rightLeg = new THREE.Mesh(legGeo, legMat);
    rightLeg.position.set(0.18, 0.2, 0);
    this.group.add(rightLeg);

    // Gun — simple box+cylinder
    const gunBody = new THREE.BoxGeometry(0.08, 0.08, 0.6);
    const gunMat = new THREE.MeshLambertMaterial({ color: 0x333333, flatShading: true });
    this.gunMesh = new THREE.Mesh(gunBody, gunMat);
    this.gunMesh.position.set(0.4, 1.1, 0.3);
    this.group.add(this.gunMesh);

    // Name tag
    this.createNameTag(playerName, teamColor);

    scene.add(this.group);
  }

  private createNameTag(name: string, color: number): void {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;

    ctx.font = 'bold 24px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#' + color.toString(16).padStart(6, '0');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 3;
    ctx.strokeText(name, 128, 38);
    ctx.fillText(name, 128, 38);

    const tex = new THREE.CanvasTexture(canvas);
    const mat = new THREE.SpriteMaterial({ map: tex, transparent: true });
    this.nameTag = new THREE.Sprite(mat);
    this.nameTag.scale.set(4, 1, 1);
    this.nameTag.position.y = 2.5;
    this.group.add(this.nameTag);
  }

  /**
   * Update infantry physics (simple simulation — no Rapier on client for now).
   */
  public update(terrain: Terrain, dt: number): void {
    if (!this.alive) return;

    // Gravity
    this.velocity.y -= 9.81 * dt;

    // Apply velocity
    this.group.position.addScaledVector(this.velocity, dt);

    // Terrain collision
    if (isInBounds(this.group.position.x, this.group.position.z)) {
      const terrainH = terrain.getHeightAt(this.group.position.x, this.group.position.z);
      if (this.group.position.y <= terrainH) {
        this.group.position.y = terrainH;
        this.velocity.y = 0;
        this.onGround = true;
      } else {
        this.onGround = false;
      }
    } else {
      this.onGround = false;
    }

    // Facing direction
    this.group.rotation.y = this.yaw;
  }

  /**
   * Move the infantry based on input, with slope-aware speed modulation.
   */
  public moveFromInput(forward: number, right: number, sprint: boolean, jump: boolean, cameraYaw: number, dt: number, terrain?: Terrain): void {
    if (!this.alive) return;

    const baseSpeed = sprint ? INFANTRY_SPRINT_SPEED : INFANTRY_MOVE_SPEED;

    // Slope check
    let speedMult = 1;
    if (terrain && isInBounds(this.group.position.x, this.group.position.z)) {
      const slopeAngle = getSlopeAngle(terrain.heightmap, this.group.position.x, this.group.position.z);
      speedMult = getSlopeSpeedMultiplier(slopeAngle, INFANTRY_MAX_SLOPE_DEG);

      // Slide downhill on steep slopes
      if (slopeAngle > INFANTRY_MAX_SLOPE_DEG * 0.7) {
        const downhill = getDownhillDirection(terrain.heightmap, this.group.position.x, this.group.position.z);
        const slideFactor = Math.min(1, (slopeAngle - INFANTRY_MAX_SLOPE_DEG * 0.7) / (INFANTRY_MAX_SLOPE_DEG * 0.3));
        this.velocity.x += downhill.x * SLOPE_SLIDE_FORCE * slideFactor * dt;
        this.velocity.z += downhill.z * SLOPE_SLIDE_FORCE * slideFactor * dt;
      }
    }

    const speed = baseSpeed * speedMult;

    // Movement direction relative to camera yaw
    const moveDir = new THREE.Vector3();
    moveDir.x = Math.sin(cameraYaw) * forward + Math.cos(cameraYaw) * right;
    moveDir.z = Math.cos(cameraYaw) * forward - Math.sin(cameraYaw) * right;

    if (moveDir.lengthSq() > 0) {
      moveDir.normalize();
      this.velocity.x = moveDir.x * speed;
      this.velocity.z = moveDir.z * speed;
      // Face movement direction
      this.yaw = Math.atan2(moveDir.x, moveDir.z);
    } else {
      this.velocity.x *= 0.85; // friction
      this.velocity.z *= 0.85;
    }

    // Jump
    if (jump && this.onGround) {
      this.velocity.y = INFANTRY_JUMP_FORCE;
      this.onGround = false;
    }
  }

  /**
   * Get the muzzle position (for hitscan / projectile origin).
   */
  public getMuzzlePosition(): THREE.Vector3 {
    const pos = new THREE.Vector3();
    this.gunMesh.getWorldPosition(pos);
    // Offset to the end of the barrel
    const forward = new THREE.Vector3(0, 0, 0.35);
    forward.applyAxisAngle(new THREE.Vector3(0, 1, 0), this.yaw);
    pos.add(forward);
    return pos;
  }

  /**
   * Get the aim direction (forward from the character + camera pitch).
   */
  public getAimDirection(cameraPitch: number): THREE.Vector3 {
    const dir = new THREE.Vector3(0, 0, 1);
    dir.applyAxisAngle(new THREE.Vector3(1, 0, 0), -cameraPitch);
    dir.applyAxisAngle(new THREE.Vector3(0, 1, 0), this.yaw);
    return dir.normalize();
  }

  public setPosition(pos: Vec3): void {
    this.group.position.set(pos.x, pos.y, pos.z);
  }

  public setHP(hp: number): void {
    this.hp = hp;
    if (hp <= 0) {
      this.alive = false;
      this.group.visible = false;
    }
  }

  public setVisible(visible: boolean): void {
    this.group.visible = visible;
  }

  public dispose(scene: THREE.Scene): void {
    scene.remove(this.group);
  }
}
